'use client';

import type { ButtonBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import { ButtonArrowLink } from './button-variants/ArrowLink';
import { ButtonPill } from './button-variants/Pill';

interface ButtonBlockRendererProps {
  block: ButtonBlock;
  editing?: boolean;
}

const styleClasses = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground',
} as const;

const sizeClasses = {
  sm: 'text-sm px-4 py-2',
  md: 'text-base px-6 py-2.5',
  lg: 'text-lg px-8 py-3',
} as const;

export function ButtonBlockRenderer({ block, editing }: ButtonBlockRendererProps) {
  switch (block.data.variant ?? 'default') {
    case 'arrow-link':
      return <ButtonArrowLink block={block} editing={editing} />;
    case 'pill':
      return <ButtonPill block={block} editing={editing} />;
    default:
      break;
  }

  const {
    text,
    url,
    style = 'primary',
    size = 'md',
    alignment = 'left',
    fullWidth = false,
    openInNewTab = false,
  } = block.data;

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  } as const;

  return (
    <div className={cn('my-6', alignClasses[alignment])}>
      <a
        href={editing ? '#' : url}
        onClick={editing ? (e) => e.preventDefault() : undefined}
        {...(!editing && openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={cn(
          'inline-block rounded-lg font-medium transition-colors',
          styleClasses[style],
          sizeClasses[size],
          fullWidth && 'block w-full text-center',
        )}
      >
        <span {...(editing ? { 'data-editable': 'data.text' } : {})}>{text}</span>
      </a>
    </div>
  );
}
